import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);

/**
 * The Reporting hub keeps every filter in the URL so a view can be linked,
 * bookmarked and reloaded. Fiscal year == calendar year.
 *
 *   /dashboard/reports?tab=&sub=&range=&start=&end=&consultant=&compare=
 */
export type RangePreset =
  | "ytd"
  | "this_quarter"
  | "last_quarter"
  | "last_year"
  | "all"
  | "custom";

export const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: "ytd", label: "YTD" },
  { value: "this_quarter", label: "This quarter" },
  { value: "last_quarter", label: "Last quarter" },
  { value: "last_year", label: "Last year" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom" },
];

export const DEFAULT_PRESET: RangePreset = "ytd";
export const DEFAULT_TAB = "overview";

export type Range = { start: Dayjs | null; end: Dayjs | null };

export const presetRange = (preset: RangePreset): Range => {
  const today = dayjs();
  switch (preset) {
    case "ytd":
      return { start: today.startOf("year"), end: today };
    case "this_quarter":
      return { start: today.startOf("quarter"), end: today };
    case "last_quarter": {
      const lastQ = today.subtract(1, "quarter");
      return { start: lastQ.startOf("quarter"), end: lastQ.endOf("quarter") };
    }
    case "last_year": {
      const lastYear = today.subtract(1, "year");
      return { start: lastYear.startOf("year"), end: lastYear.endOf("year") };
    }
    case "all":
    default:
      return { start: null, end: null };
  }
};

/**
 * The comparison window for a range, resolved per preset:
 *
 *   ytd           → the same span one year back (Jan 1 → same day last year)
 *   this_quarter  → the previous quarter, in full
 *   last_quarter  → the quarter before that one
 *   last_year     → the year before that one
 *   custom        → the same-length window ending the day before `start`
 *   all           → no comparison (returns null; the toggle is disabled)
 */
export const previousRange = (
  preset: RangePreset,
  start: Dayjs | null,
  end: Dayjs | null,
): Range | null => {
  if (preset === "all") return null;
  if (!start || !end) return null;
  switch (preset) {
    case "ytd":
      return { start: start.subtract(1, "year"), end: end.subtract(1, "year") };
    case "this_quarter":
    case "last_quarter": {
      // `start` is already the first day of the range's quarter.
      const prev = start.subtract(1, "quarter");
      return { start: prev.startOf("quarter"), end: prev.endOf("quarter") };
    }
    case "last_year": {
      const prev = start.subtract(1, "year");
      return { start: prev.startOf("year"), end: prev.endOf("year") };
    }
    case "custom":
    default: {
      const days = end.startOf("day").diff(start.startOf("day"), "day") + 1;
      if (days < 1) return null;
      const prevEnd = start.subtract(1, "day");
      return { start: prevEnd.subtract(days - 1, "day"), end: prevEnd };
    }
  }
};

export const rangeLabelFor = (range: Range | null): string => {
  if (!range || (!range.start && !range.end)) return "All time";
  const left = range.start ? range.start.format("MMM D, YYYY") : "Beginning";
  const right = range.end ? range.end.format("MMM D, YYYY") : "Today";
  return `${left} – ${right}`;
};

const parseDate = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const d = dayjs(value, "YYYY-MM-DD");
  return d.isValid() ? d : null;
};

export const fmt = (d: Dayjs | null) => (d ? d.format("YYYY-MM-DD") : "");

export interface ReportFilterState {
  tab: string;
  sub: string;
  preset: RangePreset;
  start: Dayjs | null;
  end: Dayjs | null;
  consultant: string;
  /** Compare-to-previous-period toggle (`?compare=1`). */
  compare: boolean;
  /** False when the active preset has no meaningful previous period. */
  compareAvailable: boolean;
  /** The previous-period window, or null when comparison is unavailable. */
  previous: Range | null;
  /** Human label for the previous-period window, e.g. "Jan 1, 2025 – Aug 30, 2025". */
  previousLabel: string;
  /** Human label for the active range, e.g. "Jan 1, 2026 – Today". */
  rangeLabel: string;
  /** Stable cache key for the active range + consultant. */
  filterKey: string;
  setCompare: (on: boolean) => void;
  setTab: (tab: string) => void;
  setSub: (sub: string) => void;
  setPreset: (preset: RangePreset) => void;
  setStart: (d: Dayjs | null) => void;
  setEnd: (d: Dayjs | null) => void;
  setConsultant: (id: string) => void;
  /** Patch any subset of params at once; null clears a param. */
  patch: (next: Record<string, string | null>) => void;
}

export const useReportFilter = (): ReportFilterState => {
  const [params, setParams] = useSearchParams();

  const patch = useCallback(
    (next: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          Object.entries(next).forEach(([key, value]) => {
            if (value === null || value === "") merged.delete(key);
            else merged.set(key, value);
          });
          return merged;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  const rawPreset = params.get("range") as RangePreset | null;
  const preset: RangePreset = RANGE_PRESETS.some((p) => p.value === rawPreset)
    ? (rawPreset as RangePreset)
    : DEFAULT_PRESET;

  const custom = preset === "custom";
  const fallback = presetRange(preset);
  const start = custom ? parseDate(params.get("start")) : fallback.start;
  const end = custom ? parseDate(params.get("end")) : fallback.end;

  const tab = params.get("tab") || DEFAULT_TAB;
  const sub = params.get("sub") || "";
  const consultant = params.get("consultant") || "";
  const compareRequested = params.get("compare") === "1";

  const previous = useMemo(
    () => previousRange(preset, start, end),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preset, fmt(start), fmt(end)],
  );
  const compareAvailable = previous !== null;
  const compare = compareRequested && compareAvailable;

  const setCompare = useCallback(
    (on: boolean) => patch({ compare: on ? "1" : null }),
    [patch],
  );

  const setPreset = useCallback(
    (value: RangePreset) => {
      if (value === "custom") {
        // Seed the custom pickers from whatever range is on screen now.
        const seed = presetRange(preset);
        patch({
          range: "custom",
          start: fmt(custom ? start : seed.start) || null,
          end: fmt(custom ? end : seed.end) || null,
        });
      } else {
        patch({ range: value, start: null, end: null });
      }
    },
    [patch, preset, custom, start, end],
  );

  const setStart = useCallback(
    (d: Dayjs | null) => patch({ range: "custom", start: fmt(d) || null }),
    [patch],
  );
  const setEnd = useCallback(
    (d: Dayjs | null) => patch({ range: "custom", end: fmt(d) || null }),
    [patch],
  );
  const setTab = useCallback(
    // Tab switches drop the tab-local sub-nav but keep the date/consultant filter.
    (value: string) => patch({ tab: value, sub: null }),
    [patch],
  );
  const setSub = useCallback((value: string) => patch({ sub: value }), [patch]);
  const setConsultant = useCallback(
    (id: string) => patch({ consultant: id || null }),
    [patch],
  );

  const rangeLabel = useMemo(
    () => rangeLabelFor({ start, end }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fmt(start), fmt(end)],
  );

  const previousLabel = useMemo(
    () => (previous ? rangeLabelFor(previous) : ""),
    [previous],
  );

  const filterKey = `${fmt(start)}|${fmt(end)}|${consultant}`;

  return {
    tab,
    sub,
    preset,
    start,
    end,
    consultant,
    compare,
    compareAvailable,
    previous,
    previousLabel,
    rangeLabel,
    filterKey,
    setCompare,
    setTab,
    setSub,
    setPreset,
    setStart,
    setEnd,
    setConsultant,
    patch,
  };
};

export default useReportFilter;
